import {
	type MarketControllerV2Api,
	type OutlookControllerV3Api,
	MarketControllerV2ApiFactory,
	OutlookControllerV3ApiFactory,
} from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { seconds } from "$root/utils";
import debounce from "debounce";

const { newQuery, invalidate } = repositoryHelperFactory("outlook");

const marketApi = () => getApiGen(MarketControllerV2ApiFactory);
const outlookFocusApi = () => getApiGen(OutlookControllerV3ApiFactory);

export const outlookRepository = {
	overview: newQuery("overview", () => axiosExtract(marketApi().retrieveOverview())),
	forecast: newQuery(
		"forecast",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveForecastByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveForecastByAssetClass"]>[1];
		}) => axiosExtract(marketApi().retrieveForecastByAssetClass(opts.assetClass, opts.geography)),
	),

	marketRegimeWithElementsTimeSeries: newQuery(
		"retrieveMarketRegimeWithElementsTimeSeries",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveMarketRegimeWithElementsTimeSeries"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveMarketRegimeWithElementsTimeSeries"]>[1];
			sector: SectorVariants;
			fromDate: string;
		}) =>
			marketApi().retrieveMarketRegimeWithElementsTimeSeries(
				opts.assetClass,
				opts.geography,
				opts.sector,
				opts.fromDate,
			),
	),

	marketRegimeWithElements: newQuery(
		"retrieveMarketRegimeWithElements",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveMarketRegimeWithElements"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveMarketRegimeWithElements"]>[1];
			sector: Parameters<MarketControllerV2Api["retrieveMarketRegimeWithElements"]>[2];
		}) => axiosExtract(marketApi().retrieveMarketRegimeWithElements(opts.assetClass, opts.geography, opts.sector)),
	),
	outlookFocusTimeSeriesByAssetClass: newQuery(
		"retrieveOutlookFocusTimeSeriesByAssetClass",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveOutlookFocusTimeSeriesByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveOutlookFocusTimeSeriesByAssetClass"]>[1];
			sector: SectorVariants;
			indicator: "RETURN" | "VOLATILITY" | "DIVERSIFICATION" | "REGIME";
			fromDate: string;
		}) =>
			marketApi().retrieveOutlookFocusTimeSeriesByAssetClass(
				opts.assetClass,
				opts.geography,
				opts.sector,
				opts.indicator,
				opts.fromDate,
			),
	),
	outlookTimeSeriesByAssetClass: newQuery(
		"retrieveOutlookTimeSeriesByAssetClass",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveOutlookByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveOutlookByAssetClass"]>[1];
			fromDate: string;
		}) => marketApi().retrieveOutlookTimeSeriesByAssetClass(opts.assetClass, opts.geography, opts.fromDate),
	),
	outlookTimeSeries: newQuery("retrieveOutlookTimeSeries", (opts: { fromDate: string }) =>
		marketApi().retrieveOutlookTimeSeries(opts.fromDate),
	),
	overviewTimeSeries: newQuery("retrieveOverviewTimeSeries", (opts: { fromDate: string }) =>
		marketApi().retrieveOverviewTimeSeries(opts.fromDate),
	),
	outlookTimeSeriesContinuousByAssetClass: newQuery(
		"retrieveOutlookTimeSeriesContinuousByAssetClass",
		(opts: {
			assetClass: Parameters<MarketControllerV2Api["retrieveOutlookTimeSeriesContinuousByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveOutlookTimeSeriesContinuousByAssetClass"]>[1];
			fromDate?: string;
		}) =>
			axiosExtract(
				marketApi().retrieveOutlookTimeSeriesContinuousByAssetClass(opts.assetClass, opts.geography, opts.fromDate),
			),
	),
	outlookContinuousTimeSeries: newQuery("retrieveOutlookContinuousTimeSeries", (fromDate: string) =>
		axiosExtract(marketApi().retrieveOutlookContinuousTimeSeries(fromDate)),
	),

	macroContextDriver: newQuery(
		"retrieveMacroContextDriver",
		({
			assetClass,
			geography,
			sector,
		}: {
			assetClass: Parameters<MarketControllerV2Api["retrieveMacroContextDriver"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveMacroContextDriver"]>[1];
			sector: Parameters<MarketControllerV2Api["retrieveMacroContextDriver"]>[2];
		}) => axiosExtract(marketApi().retrieveMacroContextDriver(assetClass, geography, sector)),
	),

	outlookByAssetClass: newQuery(
		"retrieveOutlookByAssetClass",
		({
			assetClass,
			geography,
		}: {
			assetClass: Parameters<MarketControllerV2Api["retrieveOutlookByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveOutlookByAssetClass"]>[1];
		}) => axiosExtract(marketApi().retrieveOutlookByAssetClass(assetClass, geography)),
	),

	outlook: newQuery("retrieveOutlook", () => axiosExtract(marketApi().retrieveOutlook())),

	varByAssetClass: newQuery(
		"retrieveVarByAssetClass",
		({
			assetClass,
			geography,
			sector,
		}: {
			assetClass: Parameters<MarketControllerV2Api["retrieveVarByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveVarByAssetClass"]>[1];
			sector: Parameters<MarketControllerV2Api["retrieveVarByAssetClass"]>[2];
		}) => axiosExtract(marketApi().retrieveVarByAssetClass(assetClass, geography, sector)),
	),

	acMetricsStandard: newQuery(
		"retrieveAcMetricsStandard",
		({
			assetClass,
			geography,
			sector,
		}: {
			assetClass: Parameters<OutlookControllerV3Api["retrieveAcMetricsStandard"]>[0];
			geography: Parameters<OutlookControllerV3Api["retrieveAcMetricsStandard"]>[1];
			sector: Parameters<OutlookControllerV3Api["retrieveAcMetricsStandard"]>[2];
		}) => axiosExtract(outlookFocusApi().retrieveAcMetricsStandard(assetClass, geography, sector)),
	),

	positioningContinuousTimeSeries: newQuery(
		"retrievePositioningContinuousTimeSeries",
		({
			assetClass,
			geography,
			sector,
			fromDate,
		}: {
			assetClass: Parameters<MarketControllerV2Api["retrievePositioningContinuousTimeSeries"]>[0];
			geography: Parameters<MarketControllerV2Api["retrievePositioningContinuousTimeSeries"]>[1];
			sector: Parameters<MarketControllerV2Api["retrievePositioningContinuousTimeSeries"]>[2];
			fromDate?: string;
		}) => axiosExtract(marketApi().retrievePositioningContinuousTimeSeries(assetClass, geography, sector, fromDate)),
	),

	factorsByAssetClass: newQuery(
		"retrieveFactorsByAssetClass",
		({
			assetClass,
			geography,
			sector,
		}: {
			assetClass: Parameters<MarketControllerV2Api["retrieveFactorsByAssetClass"]>[0];
			geography: Parameters<MarketControllerV2Api["retrieveFactorsByAssetClass"]>[1];
			sector: Parameters<MarketControllerV2Api["retrieveFactorsByAssetClass"]>[2];
		}) => axiosExtract(marketApi().retrieveFactorsByAssetClass(assetClass, geography, sector)),
	),
} as const;

eventBus.addListener(
	"market-update",
	debounce(() => {
		invalidate();
	}, seconds(2)),
);

export type SectorVariants =
	| "GLOBAL"
	| "PRECIOUS_METALS"
	| "ENERGY"
	| "FINANCIALS"
	| "INDUSTRIALS"
	| "CONSUMER_STAPLES"
	| "COMMUNICATION_SERVICES"
	| "INFORMATION_TECHNOLOGY"
	| "CONSUMER_DISCRETIONARY"
	| "HEALTHCARE"
	| "MATERIALS"
	| "UTILITIES"
	| "REAL_ESTATE"
	| "AGRICULTURE"
	| "INDUSTRIAL_METALS"
	| "LONG_GOV"
	| "MEDIUM_GOV"
	| "SHORT_GOV"
	| "CORP"
	| "GOV"
	| "HY"
	| "ALL";
